import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtNum } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FolderOpen, Eye, Receipt, Warning } from "@phosphor-icons/react";
import WelcomeQuickStart from "@/components/WelcomeQuickStart";

/**
 * Tier 2 dashboard — Case Manager / Ops view.
 * Shows: my active cases · sightings pending my action · expenses · unread escalations.
 */
export default function CaseManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ cases: [], sightings: [], escalations: [], claims: [] });

  useEffect(() => { (async () => {
    try {
      const [cs, sg, esc, cl] = await Promise.all([
        api.get("/cases").catch(() => ({ data: [] })),
        api.get("/sighting-reports").catch(() => ({ data: [] })),
        api.get("/escalations").catch(() => ({ data: [] })),
        api.get("/expenses/claims?mine=true").catch(() => ({ data: [] })),
      ]);
      const myCases = (cs.data || []).filter(c =>
        c.assignee_user_id === user?.user_id ||
        c.rm_user_id === user?.user_id ||
        c.assignee === user?.name
      ).slice(0, 10);
      const pendingSightings = (sg.data || []).filter(s =>
        ["submitted", "under_review", "pending"].includes(s.status)
      ).slice(0, 5);
      setData({
        cases: myCases.length ? myCases : (cs.data || []).slice(0, 10),
        sightings: pendingSightings,
        escalations: (esc.data || []).slice(0, 5),
        claims: (cl.data || []).slice(0, 5),
      });
    } catch (e) { /* ignore */ }
  })(); }, [user?.user_id]);

  return (
    <div className="space-y-6" data-testid="case-manager-dashboard">
      <div>
        <div className="label-eyebrow">Case Manager · Operations</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Your cases & actions</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1 font-mono">Welcome back, {user?.name}.</div>
      </div>

      {/* First-visit Welcome / Quick-Start tile (auto-hides after dismiss or ≥3 sightings filed) */}
      <WelcomeQuickStart
        taskCount={(data.cases.length || 0) + (data.sightings.length || 0)}
        sightingCount={data.sightings.length}
        variant="manager"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="My active cases" icon={FolderOpen} testid="cm-cases" count={data.cases.length} link="/cases">
          {data.cases.length === 0 ? <Empty>No assigned cases.</Empty> : (
            <ul className="divide-y divide-[var(--span-border)]">
              {data.cases.map((c, i) => (
                <li key={c.case_id || `case-${i}`} className="px-5 py-3 flex items-center gap-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--span-text-2)] w-[110px] shrink-0 truncate">{c.case_code || c.case_id?.slice(0,12)}</div>
                  <Link to={`/cases/${c.case_id}`} className="text-[13px] font-medium truncate flex-1 hover:underline">{c.title || "Untitled"}</Link>
                  <span className={`badge ${c.status === "open" ? "badge-info" : c.status === "closed" ? "badge-success" : "badge-warn"}`}>{c.status || "open"}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Sightings pending action" icon={Eye} testid="cm-sightings" count={data.sightings.length} link="/reports/sighting">
          {data.sightings.length === 0 ? <Empty>None pending.</Empty> : (
            <ul className="divide-y divide-[var(--span-border)]">
              {data.sightings.map((s, i) => (
                <li key={s.report_id || `sg-${i}`} className="px-5 py-3">
                  <Link to={`/reports/sighting/${s.report_id}`} className="text-[13px] font-medium truncate hover:underline">{s.title || s.summary?.slice(0,60) || "Sighting"}</Link>
                  <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-0.5">{fmtDate(s.submitted_at)} · {s.status || "submitted"}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Unread escalations" icon={Warning} testid="cm-escalations" count={data.escalations.length} link="/escalations">
          {data.escalations.length === 0 ? <Empty>All clear.</Empty> : (
            <ul className="divide-y divide-[var(--span-border)]">
              {data.escalations.map((e, i) => (
                <li key={e.escalation_id || e.id || `esc-${i}`} className="px-5 py-3 text-[13px]">
                  <div className="font-medium truncate">{e.title || e.reason}</div>
                  <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-0.5">{fmtDate(e.created_at)} · {e.severity || "high"}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="My recent expense claims" icon={Receipt} testid="cm-claims" count={data.claims.length} link="/expenses">
          {data.claims.length === 0 ? <Empty>No claims submitted.</Empty> : (
            <ul className="divide-y divide-[var(--span-border)]">
              {data.claims.map((c, i) => (
                <li key={c.claim_id || `cl-${i}`} className="px-5 py-3 flex items-center gap-3">
                  <div className="text-[13px] flex-1 truncate">{c.purpose?.slice(0,50) || "Claim"}</div>
                  <div className="font-mono text-[12px] tnum">₹{fmtNum(c.total_inr || 0)}</div>
                  <span className={`badge ${c.status === "approved" ? "badge-success" : "badge-info"}`}>{c.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, link, children, testid }) {
  return (
    <div className="card-flat" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[var(--span-text-2)]" />}
          <div className="label-eyebrow">{title}</div>
          {typeof count === "number" && <span className="font-mono text-[10px] text-[var(--span-text-2)]">· {count}</span>}
        </div>
        <Link to={link} className="text-[11px] text-[var(--span-text-2)] hover:text-[var(--span-text)] font-mono uppercase tracking-[0.12em]">View all →</Link>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="px-5 py-6 text-center text-[12px] text-[var(--span-text-2)]">{children}</div>;
}
